/**
 * Periodic update functions for forecast, radar, warnings, sensors
 */

import { API_ENDPOINTS } from '../config.js';
import { setLastCheck, setSensorHealth } from './state.js';
import { setContentHeight } from '../utils/dom.js';

/**
 * Per-sensor temperature thresholds in °F used to color the reading
 * (below -> too cold, above -> too warm). Keys are matched as substrings
 * against the device name; the first match wins, otherwise
 * DEFAULT_TEMP_THRESHOLDS is used.
 *
 * A `null` band (e.g. Attic/Garage) means that direction never alerts, so
 * the sensor always shows the normal (green) background. A `null` rule
 * disables color coding entirely.
 */
const TEMP_THRESHOLDS = {
  Fridge: { below: 32, above: 40 },
  Attic: { below: null, above: null },
  Garage: { below: null, above: null },
  Server: { below: 50, above: 90 }, // matches "Server" and "Server Room"
};
const DEFAULT_TEMP_THRESHOLDS = { below: 65, above: 75 };

/**
 * Resolve the temperature threshold rule for a device by name.
 * @param {string} deviceName - Sensor device name
 * @returns {{below: number, above: number}|null} Threshold rule, or null if coloring is disabled
 */
function getTempThresholds(deviceName) {
  const match = Object.keys(TEMP_THRESHOLDS).find((name) => deviceName.includes(name));
  return match === undefined ? DEFAULT_TEMP_THRESHOLDS : TEMP_THRESHOLDS[match];
}

/**
 * Show loading state for a status indicator
 * @param {string} statusId - ID of the status indicator element
 */
function setLoadingState(statusId) {
  jQuery(statusId).removeClass('success alert').addClass('secondary');
  // Trigger health status update after a short delay to ensure DOM is updated
  setTimeout(() => updateHealthStatus(), 50);
}

/**
 * Show success state for a status indicator
 * @param {string} statusId - ID of the status indicator element
 */
function setSuccessState(statusId) {
  jQuery(statusId).removeClass('secondary alert').addClass('success');
  // Trigger health status update after a short delay to ensure DOM is updated
  setTimeout(() => updateHealthStatus(), 50);
}

/**
 * Show error state for a status indicator
 * @param {string} statusId - ID of the status indicator element
 */
function setErrorState(statusId) {
  jQuery(statusId).removeClass('secondary success').addClass('alert');
  // Trigger health status update after a short delay to ensure DOM is updated
  setTimeout(() => updateHealthStatus(), 50);
}

/**
 * Display error notification to user
 * @param {string} message - Error message to display
 */
function showErrorNotification(message) {
  // Create error notification element if it doesn't exist
  if (jQuery('#error-notification').length === 0) {
    jQuery('body').append('<div id="error-notification" class="callout alert" style="position: fixed; top: 10px; right: 10px; z-index: 9999; max-width: 400px; display: none;"></div>');
  }

  jQuery('#error-notification').html(`<strong>Error:</strong> ${  message}`).fadeIn().delay(5000).fadeOut();
}

/**
 * Update forecast data
 */
export function updateForecast() {
  setLoadingState('#wsStatusForecast');

  jQuery.getJSON(API_ENDPOINTS.FORECAST, {}, (data) => {
    jQuery('#wx-bar .forecast-day').remove();
    jQuery('#wx-bar').append(data['d']['daily']);
    jQuery('#hourly-forecast-holder').html(data['d']['hourly']);
    jQuery('#wsUpdateForecast').text(new Date().toLocaleTimeString());
    setSuccessState('#wsStatusForecast');
    setContentHeight();
    setLastCheck('Forecast', new Date());
  }).fail((jqXHR, textStatus, errorThrown) => {
    console.error('[Updates] Failed to update forecast:', textStatus, errorThrown);
    console.error('[Updates] Response:', jqXHR.responseText);
    setErrorState('#wsStatusForecast');
    showErrorNotification('Failed to update weather forecast');
  });
}

/**
 * Update radar images (local, modal, and national)
 */
export function updateRadar() {
  setLoadingState('#wsStatusRadar');

  const lRadar = `https://radar.weather.gov/ridge/standard/KLWX_loop.gif?${  Date.now()}`;
  const nRadar = `https://radar.weather.gov/ridge/standard/CONUS-LARGE_loop.gif?${  Date.now()}`;

  // Set up image load handlers to track success/failure
  let loadedCount = 0;
  let errorCount = 0;
  const totalImages = 3;

  const checkComplete = function () {
    if (loadedCount + errorCount === totalImages) {
      if (errorCount > 0) {
        setErrorState('#wsStatusRadar');
        showErrorNotification(`Failed to load ${  errorCount  } radar image(s)`);
      } else {
        setSuccessState('#wsStatusRadar');
        setLastCheck('Radar', new Date());
      }
      jQuery('#wsUpdateRadar').text(new Date().toLocaleTimeString());
    }
  };

  jQuery('#radar-local').off('load error').on('load', () => {
    loadedCount++;
    checkComplete();
  }).on('error', () => {
    errorCount++;
    console.error('[Updates] Failed to load local radar');
    checkComplete();
  }).attr('src', lRadar);

  jQuery('#radar-local-modal').off('load error').on('load', () => {
    loadedCount++;
    checkComplete();
  }).on('error', () => {
    errorCount++;
    console.error('[Updates] Failed to load local radar modal');
    checkComplete();
  }).attr('src', lRadar);

  jQuery('#radar-national').off('load error').on('load', () => {
    loadedCount++;
    checkComplete();
  }).on('error', () => {
    errorCount++;
    console.error('[Updates] Failed to load national radar');
    checkComplete();
  }).attr('src', nRadar);
}

/**
 * Update weather warnings
 */
export function updateWarnings() {
  setLoadingState('#wsStatusWarnings');

  jQuery.getJSON(API_ENDPOINTS.WARNINGS, {}, (data) => {
    jQuery('#weather-alerts').html('');

    // Store warnings globally for modal access
    window.warnings = {};

    jQuery.each(data['d'], (k, v) => {
      const warningButton = jQuery(`<span class="weather-alert label status-label alert" data-open="modal-warning" data-alertid="${  v['id']  }">${  v['event']  }</span>`);
      jQuery('#weather-alerts').append(warningButton);
      window.warnings[v['id']] = v;
    });

    jQuery('#wsUpdateWarnings').text(new Date().toLocaleTimeString());
    setSuccessState('#wsStatusWarnings');
    setLastCheck('Warnings', new Date());
  }).fail((jqXHR, textStatus, errorThrown) => {
    console.error('[Updates] Failed to update warnings:', textStatus, errorThrown);
    console.error('[Updates] Response:', jqXHR.responseText);
    setErrorState('#wsStatusWarnings');
    showErrorNotification('Failed to update weather warnings');
  });
}

/**
 * Build an inline SVG sparkline from a series of values.
 * @param {number[]} values - Chronological readings
 * @param {string} color - Stroke color
 * @returns {string} SVG markup (empty string when there isn't enough data)
 */
function buildSparkline(values, color) {
  if (!Array.isArray(values) || values.length < 2) {
    return '';
  }

  const w = 100;
  const h = 20;
  const pad = 2;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const n = values.length;

  const points = values.map((val, i) => {
    const x = (i / (n - 1)) * w;
    const y = h - pad - ((val - min) / range) * (h - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  return `<svg class="spark" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none">`
    + `<polyline points="${points}" fill="none" stroke="${color}" stroke-width="1.5" `
    + 'vector-effect="non-scaling-stroke" stroke-linejoin="round" stroke-linecap="round" /></svg>';
}

/**
 * Update sensor data (Govee temperature/humidity/leak sensors)
 */
export function updateSensors() {
  setLoadingState('#wsStatusSensors');

  jQuery.getJSON(API_ENDPOINTS.SENSORS, {}, (data) => {
    jQuery.each(data, (k, v) => {
      if (v['temperature']) {
        // Temperature sensor
        const curTemp = Math.round(v['temperature']);
        const tempFinite = Number.isFinite(curTemp);

        jQuery(`#temperature-${  k}`).text(tempFinite ? `${curTemp}°F` : '--');
        jQuery(`#temperature-${  k}`).parent().removeClass((index, className) => {
          return (className.match(/(^|\s)sensor-temperature-\S+/g) || []).join(' ');
        });

        // Determine temperature status from the sensor's threshold rule.
        // A null rule (e.g. Attic, Garage) disables color coding entirely.
        const thresholds = getTempThresholds(v['deviceName']);
        let status = 'normal';

        if (thresholds) {
          if (typeof thresholds.above === 'number' && curTemp > thresholds.above) {
            status = 'above';
          } else if (typeof thresholds.below === 'number' && curTemp < thresholds.below) {
            status = 'below';
          }
        }

        // Only apply status when the reading is live and the sensor is color-coded
        if (thresholds && tempFinite && v['battery'] > 0) {
          jQuery(`#temperature-${  k}`).parent().addClass(`sensor-temperature-${  status}`);
        }

        // Humidity (may be 'missing' when the sensor reading is stale)
        const curHum = Math.round(v['humidity']);
        const humElement = jQuery(`#humidity-${  k}`);
        humElement.text(Number.isFinite(curHum) ? `${curHum}%` : '--');
        humElement.parent().removeClass((index, className) => {
          return (className.match(/(^|\s)sensor-humidity-\S+/g) || []).join(' ');
        });

        // Color humidity like temperature, but only alert when above 60%
        if (Number.isFinite(curHum) && v['battery'] > 0) {
          const humStatus = curHum > 60 ? 'above' : 'normal';
          humElement.parent().addClass(`sensor-humidity-${  humStatus}`);
        }

        // Daily high / low shown directly under each value
        if (tempFinite && Number.isFinite(v['tempMax'])) {
          jQuery(`#hilo-${k}`).html(`&darr;${Math.round(v['tempMin'])} &uarr;${Math.round(v['tempMax'])}`);
        } else {
          jQuery(`#hilo-${k}`).text('');
        }
        if (Number.isFinite(curHum) && Number.isFinite(v['humidityMax'])) {
          jQuery(`#humidity-hilo-${k}`).html(`&darr;${Math.round(v['humidityMin'])} &uarr;${Math.round(v['humidityMax'])}`);
        } else {
          jQuery(`#humidity-hilo-${k}`).text('');
        }

        // 24h sparklines (white line reads on the colored box)
        jQuery(`#spark-${k}`).html(buildSparkline(v['tempSeries'], 'rgba(255,255,255,0.9)'));
        jQuery(`#humidity-spark-${k}`).html(buildSparkline(v['humiditySeries'], 'rgba(255,255,255,0.9)'));
      } else {
        // Leak sensor
        jQuery(`#leak-${  k}`).removeClass((index, className) => {
          return (className.match(/(^|\s)sensor-leak-\S+/g) || []).join(' ');
        });
        jQuery(`#leak-${  k}`).addClass('sensor-leak-no');
      }

      // Update battery indicator
      jQuery(`#battery-${  k}`).val(v['battery']);

      // Surface a low battery (still reporting but running down)
      const battery = v['battery'];
      const lowBattery = typeof battery === 'number' && battery > 0 && battery <= 20;
      jQuery(`#battery-pct-${k}`).text(lowBattery ? `${battery}%` : '');
      jQuery(`#battery-${k}`).toggleClass('battery-low', lowBattery);

      // Dim and badge sensors that are offline (not returning data)
      jQuery(`#temperature-${k}, #leak-${k}`)
        .closest('.sensor-holder, .sensor-leak-holder')
        .toggleClass('sensor-offline', v['offline'] === true);
    });

    // Summarize sensor health for the system-status panel
    let sensorTotal = 0;
    let offlineCount = 0;
    let lowBatteryCount = 0;
    jQuery.each(data, (k, v) => {
      sensorTotal += 1;
      if (v['offline'] === true) {
        offlineCount += 1;
      }
      const battery = v['battery'];
      if (typeof battery === 'number' && battery > 0 && battery <= 20) {
        lowBatteryCount += 1;
      }
    });
    setSensorHealth({ total: sensorTotal, offline: offlineCount, lowBattery: lowBatteryCount });

    jQuery('#wsUpdateSensors').text(new Date().toLocaleTimeString());
    setSuccessState('#wsStatusSensors');
    setLastCheck('Sensors', new Date());
  }).fail((jqXHR, textStatus, errorThrown) => {
    console.error('[Updates] Failed to update sensors:', textStatus, errorThrown);
    console.error('[Updates] Response:', jqXHR.responseText);
    setErrorState('#wsStatusSensors');
    showErrorNotification('Failed to update sensor data');
  });
}

/**
 * Calculate and update overall health status based on all sub-statuses
 */
export function updateHealthStatus() {
  const statusElements = [
    '#wsStatusTempest',
    '#wsStatusWeather',
    '#wsStatusRadar',
    '#wsStatusWarnings',
    '#wsStatusForecast',
    '#wsStatusSensors',
    '#wsStatusChore',
    '#wsStatusAppliances',
  ];

  let hasError = false;
  let hasLoading = false;
  let allSuccess = true;

  // Check each sub-status
  statusElements.forEach(elementId => {
    const $element = jQuery(elementId);
    if ($element.hasClass('alert')) {
      hasError = true;
      allSuccess = false;
    } else if ($element.hasClass('secondary')) {
      hasLoading = true;
      allSuccess = false;
    } else if (!$element.hasClass('success')) {
      allSuccess = false;
    }
  });

  // Update overall health status icon based on priority: error > loading > success
  const $healthStatus = jQuery('#wsStatusHealth');
  $healthStatus.removeClass('alert secondary success');

  if (hasError) {
    $healthStatus.addClass('alert');
  } else if (hasLoading) {
    $healthStatus.addClass('secondary');
  } else if (allSuccess) {
    $healthStatus.addClass('success');
  } else {
    // Default to secondary if status is unclear
    $healthStatus.addClass('secondary');
  }

  // Update health status timestamp
  jQuery('#wsUpdateHealth').text(new Date().toLocaleTimeString());
}

